import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="w-full min-h-screen bg-canvas text-text-primary py-24 px-6 md:px-12 relative">
      {/* Close Button - Top Right */}
      <nav className="fixed top-0 left-0 right-0 z-50 pointer-events-none safe-top">
        <div className="relative pointer-events-auto flex items-center justify-end h-20 px-6 md:px-12 w-full">
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-gray-300 transition-colors p-2 focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-4 focus:outline-none z-10"
            aria-label="Close privacy policy"
          >
            <svg 
              className="w-6 h-6" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto">
        <h1 className="text-5xl md:text-7xl font-light tracking-[-0.02em] mb-4 text-center">
          Privacy Policy
        </h1>
        <p className="text-sm text-text-secondary text-center opacity-60 mb-12">
          Effective Date: December 2, 2025
        </p>
        
        <div className="space-y-8 text-text-secondary font-light leading-relaxed">
          <section>
            <p className="text-lg text-text-secondary">
              This Privacy Policy explains how we collect, use, disclose, and protect the personal information you provide or that we collect when you access or use our website (nurameditation.com), including when you join our waitlist. It explains your privacy rights and choices, and how to contact us about our privacy practices. Please read this Policy carefully. By using our website or joining our waitlist, you consent to the data practices described in this Policy. Note: This Policy applies to our website and waitlist. When our mobile application launches, a separate privacy policy may apply to app usage.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              Entity and Contact Information
            </h2>
            <p className="text-lg text-text-secondary mb-4">
              This Privacy Policy is published on behalf of our business entity operating in WY, United States.
            </p>
            <p className="text-lg text-text-secondary mb-2">
              <strong className="text-text-primary">Business Name:</strong> SilentLabs LLC
            </p>
            <p className="text-lg text-text-secondary mb-4">
              <strong className="text-text-primary">Registered Address:</strong> 30 N Gould St Ste 52683, Sheridan
            </p>
            <p className="text-lg text-text-secondary mb-4">
              All references to "we", "us", or "our" refer to the business entity identified above.
            </p>
            <p className="text-lg text-text-secondary">
              <strong className="text-text-primary">How to Contact Us:</strong> If you have questions, requests, or concerns about this Privacy Policy, please contact us at:{' '}
              <a href="mailto:support@nurameditation.com" className="text-text-primary hover:opacity-60 transition-opacity">
                support@nurameditation.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              1. Information We Collect
            </h2>
            <p className="text-lg text-text-secondary mb-4">
              We collect multiple types of information to operate effectively and provide you the best experience. The categories of information we collect include:
            </p>
            
            <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
              1.1. Information You Provide Directly
            </h3>
            <p className="text-lg text-text-secondary">
              When you join our waitlist, contact us, or otherwise communicate with us through our website, you may provide personal information such as your email address and any other information you choose to include in your communications.
            </p>

            <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
              1.2. Automatically Collected Information
            </h3>
            <p className="text-lg text-text-secondary">
              We automatically collect technical and usage data when you interact with our website, which may include IP address, device identifiers, device model and operating system, browser type, language preferences, access times, pages visited, referring/exit pages, and performance metrics. This information is collected through Vercel Analytics, which does not collect personal data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              2. How We Use Information
            </h2>
            <p className="text-lg text-text-secondary mb-4">
              We use collected information for the following purposes:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-secondary ml-4">
              <li>To maintain and manage our waitlist and communicate with you about early access opportunities.</li>
              <li>To send you updates about our product launch, including invitations to use our mobile application when it becomes available.</li>
              <li>To provide customer support and respond to your inquiries.</li>
              <li>To perform analytics, monitor website performance, and produce aggregated statistics for internal use.</li>
              <li>To comply with legal obligations, respond to lawful requests from government or law enforcement, and protect rights and safety.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              3. Legal Bases for Processing
            </h2>
            <p className="text-lg text-text-secondary mb-4">
              To the extent applicable, our lawful bases for processing personal data may include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-secondary ml-4">
              <li><strong className="text-text-primary">Consent:</strong> You have given consent by voluntarily joining our waitlist and providing your email address.</li>
              <li><strong className="text-text-primary">Legitimate Interests:</strong> Processing necessary for our legitimate interests, balanced against your rights (e.g., maintaining our waitlist, communicating product updates, network security).</li>
              <li><strong className="text-text-primary">Legal Obligation:</strong> Processing required to comply with law or regulatory requests.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              4. Cookies and Tracking Technologies
            </h2>
            <p className="text-lg text-text-secondary">
              We do not use cookies for tracking beyond essential technical cookies required to operate the service. To manage cookies, you may use your browser settings or device controls.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              5. Waitlist and Marketing Communications
            </h2>
            <p className="text-lg text-text-secondary mb-4">
              By joining our waitlist, you consent to receive communications from us regarding early access opportunities, product launch updates, and invitations to use our mobile application. We may use your email address to send you these communications.
            </p>
            <p className="text-lg text-text-secondary mb-4">
              You may opt out of marketing communications at any time by clicking the unsubscribe link in any email we send, or by contacting us at{' '}
              <a href="mailto:support@nurameditation.com" className="text-text-primary hover:opacity-60 transition-opacity">
                support@nurameditation.com
              </a>
              . Please note that even if you opt out of marketing communications, we may still send you essential service-related messages (e.g., waitlist confirmation, app launch invitations).
            </p>
            <p className="text-lg text-text-secondary">
              When our mobile application launches, we may transfer your waitlist email address to our app platform to facilitate your account creation or to send you an invitation to download and use the app. At that time, a separate privacy policy will govern app usage and data collection.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              6. Advertising and Analytics
            </h2>
            <p className="text-lg text-text-secondary">
              We do not display advertisements from third-party networks that use tracking for ad targeting. We use Vercel Analytics for website usage metrics, which does not collect personal data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              7. Sharing and Disclosure
            </h2>
            <p className="text-lg text-text-secondary mb-4">
              We do not sell your personal information. We disclose personal information only as described below or with your consent:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-secondary ml-4">
              <li>To service providers and contractors providing hosting, infrastructure, and other services to operate our website (including Supabase for data storage and Vercel for hosting and analytics).</li>
              <li>To comply with legal processes, such as court orders, subpoenas, or government investigations.</li>
              <li>To protect our rights, property, or safety, or the rights, property, or safety of others.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              8. International Data Transfers
            </h2>
            <p className="text-lg text-text-secondary">
              Our operations may involve transferring personal information to countries outside your country of residence. When we transfer data internationally, we implement appropriate safeguards, such as standard contractual clauses or other mechanisms permitted by law, to protect the data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              9. Data Retention and Deletion
            </h2>
            <p className="text-lg text-text-secondary mb-4">
              We retain waitlist email addresses until you request deletion, or until we determine that the information is no longer necessary for the purposes for which it was collected (e.g., after our product launch and after we have provided you with an opportunity to use the application). We may retain your email address longer if required by law or for legitimate business purposes, such as maintaining records of opt-out requests.
            </p>
            <p className="text-lg text-text-secondary mb-4">
              When our mobile application launches, we may retain your waitlist email address to facilitate your account creation or to send you an invitation. At that time, data retention will be governed by the app's privacy policy.
            </p>
            <p className="text-lg text-text-secondary">
              You may request deletion of your waitlist data at any time by contacting us at{' '}
              <a href="mailto:support@nurameditation.com" className="text-text-primary hover:opacity-60 transition-opacity">
                support@nurameditation.com
              </a>
              . We will process deletion requests in accordance with applicable law and our internal retention policies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              10. Security Measures
            </h2>
            <p className="text-lg text-text-secondary">
              We use administrative, technical, and physical controls designed to protect personal information from unauthorized access, disclosure, alteration, and destruction. We work with trusted service providers (including Supabase and Vercel) that implement industry-standard security measures, including encryption in transit and at rest, secure data centers, and access controls. While we strive to use reasonable safeguards, no system can be guaranteed to be 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              11. Children's Privacy
            </h2>
            <p className="text-lg text-text-secondary">
              Our website is not directed to children under the age of 16 (or the minimum age in your jurisdiction, which may be 13 in some regions). We do not knowingly collect personal information from children under the applicable age without parental consent. If a parent or guardian believes we have collected information from a child under the applicable age without consent, please contact us at{' '}
              <a href="mailto:support@nurameditation.com" className="text-text-primary hover:opacity-60 transition-opacity">
                support@nurameditation.com
              </a>
              {' '}and we will take steps to delete such data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              12. Your Privacy Rights
            </h2>
            <p className="text-lg text-text-secondary mb-4">
              Depending on where you reside, you may have rights including the right to access, correct, or delete your personal data, the right to portability, the right to restrict or object to processing, and the right to withdraw consent. To exercise these rights, contact us at{' '}
              <a href="mailto:support@nurameditation.com" className="text-text-primary hover:opacity-60 transition-opacity">
                support@nurameditation.com
              </a>
              . We will verify requests consistent with applicable law and respond within applicable timeframes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              13. Rights for European Residents (GDPR)
            </h2>
            <p className="text-lg text-text-secondary">
              This Privacy Policy is written to be consistent with the rights provided under the European Union General Data Protection Regulation (GDPR). If you are located in the European Economic Area (EEA), you may have additional rights under the GDPR. Under GDPR, you may request access to your personal data, rectification, erasure, restriction of processing, data portability, and to object to processing. In some cases, you can lodge a complaint with a supervisory authority where you believe your rights have been violated.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              14. Rights for California Residents (CCPA/CPRA)
            </h2>
            <p className="text-lg text-text-secondary">
              If you are a California resident, you may have rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA). You can request details about the categories of personal data we collect, the categories of sources, the categories of recipients, and the business purposes for collection. You may request deletion and opt-out of sale or sharing where applicable. We do not knowingly sell the personal information of minors under 16 without affirmative authorization.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              15. Third-Party Links and Integrations
            </h2>
            <p className="text-lg text-text-secondary">
              Our website may contain links to third-party websites or integrate with third-party services. We are not responsible for their privacy practices. Review their privacy policies before providing personal information to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              16. Changes to This Policy
            </h2>
            <p className="text-lg text-text-secondary">
              We may update this Privacy Policy from time to time. When we make material changes, we will provide notice on our website or by other means as required by law. The updated Policy will indicate the Effective Date. Continued use of our website or waitlist after changes constitutes your acceptance of the revised Policy.
            </p>
          </section>

          <section className="pt-8 border-t border-white/10">
            <p className="text-sm text-text-secondary opacity-60">
              Publisher: SilentLabs LLC — 30 N Gould St Ste 52683, Sheridan.
            </p>
            <p className="text-sm text-text-secondary opacity-60 mt-2">
              Last updated: December 2, 2025
            </p>
          </section>
        </div>

        <div className="mt-16 text-center">
          <Link 
            to="/" 
            className="text-ui uppercase tracking-[0.05em] text-text-primary opacity-60 hover:opacity-100 transition-opacity"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
